import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import TransitionLink from '../shell/TransitionLink.jsx';
import YouTubePlayer from '../videography/YouTubePlayer.jsx';
import styles from './SceneFilm.module.css';

// ─── Films ────────────────────────────────────────────────────────────────────
const FILMS = [
  { id: 'f12', title: 'THE OFFSPRING',       sub: "YOU'RE GONNA GO FAR KID", src: 'https://m5liydn1rzm6kmjj.public.blob.vercel-storage.com/media/6a8ca5b1e38da72cb69af7f3-ggfk_Horiz_final.mp4' },
  // zoom/origin reframe the backdrop for a single film. Scaling about a point
  // above and left of centre keeps that region in view and pushes the rest out.
  // A film can carry a youtubeId instead of an mp4 src — the backdrop then swaps
  // the <video> for a muted, chrome-free embed. The videography page keeps its
  // own (different) Unwritten cut; these two lists are deliberately separate.
  { id: 'f11', title: 'NATASHA BEDINGFIELD', sub: 'UNWRITTEN',               youtubeId: 'QnjiUJ4SvbQ', width: 1920, height: 1080 },
  { id: 'f14', title: 'WILLOW',              sub: 'VHS',                     src: 'https://m5liydn1rzm6kmjj.public.blob.vercel-storage.com/media/6a8f0c84c1c37e541839b15f-edit_4.mp4' },
  { id: 'f15', title: 'BELLA KAY',           sub: 'WOMEN ON THE RISE (LIVENATION WOMEN)', youtubeId: 'WEYIgwf6flg', width: 1080, height: 1920, poster: '/films/poster/N028.webp' },
  { id: 'f16', title: 'SUKI WATERHOUSE',     sub: 'NOSTALGIA (CONCERT RECAP)', youtubeId: 'ojln2TDFqKs', width: 1080, height: 1920, poster: '/films/poster/N029.webp' },
  { id: 'f06', title: 'ROLE MODEL',          sub: 'SHOW REEL',               src: 'https://m5liydn1rzm6kmjj.public.blob.vercel-storage.com/media/69ebd971f38b1e10f23a60db-Edit_02.mp4' },
  { id: 'f01', title: 'THE WEEKND',          sub: 'LIVE FILM',               src: 'https://m5liydn1rzm6kmjj.public.blob.vercel-storage.com/media/69e400f527103b4455595ebf-The_Weeknd.mp4' },
  { id: 'f09', title: 'JADE LEMAC',          sub: 'RECAP',                   src: 'https://m5liydn1rzm6kmjj.public.blob.vercel-storage.com/media/6a8f17ca7323180a7dcf502e-Recap_Horizontal_compressed.mp4' },
  { id: 'f04', title: 'RENEE RAPP',          sub: 'SHOW REEL',               src: 'https://m5liydn1rzm6kmjj.public.blob.vercel-storage.com/media/69ebd92fa374be3b2353c8db-Edit_Final.mp4' },
  { id: 'f02', title: 'OLIVIA RODRIGO',      sub: 'STOP MOTION',             src: 'https://m5liydn1rzm6kmjj.public.blob.vercel-storage.com/media/69ec14d1cf543316b704363e-Olivia_Rodrigo_Edit_2.mp4' },
];

// ─── YouTube embeds ───────────────────────────────────────────────────────────
// Backdrop: muted, looping, no controls or branding, and the JS API enabled so
// the sound toggle can reach it. The fullscreen player is YouTubePlayer — the one
// the videography card uses, laid out at 1080p so YouTube serves 1080p.
const ytBackdropSrc = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}` +
  '&controls=0&disablekb=1&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1&enablejsapi=1';
function ytCommand(frame, func) {
  try {
    frame?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args: [] }), '*',
    );
  } catch (_) { /* cross-origin before the player is ready */ }
}

// key for the crossfade: an mp4 url, or the embed marked as one
const sourceKey = (film) => (film.youtubeId ? `yt:${film.youtubeId}` : film.src);

// Names longer than the rail was designed for (OLIVIA RODRIGO, 14ch) are scaled
// down so they bleed over the video no further than the existing titles do.
const NAME_FITS_CH = 13;
const NAME_FONT_PX = 32;   // must track .filmItemName in SceneFilm.module.css
function nameSizeFor(title) {
  if (title.length <= NAME_FITS_CH) return undefined;
  return { fontSize: `${((NAME_FONT_PX * NAME_FITS_CH) / title.length).toFixed(1)}px` };
}

// ─── Speaker icons (match Videography.jsx) ────────────────────────────────────
function IconMuted() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );
}
function IconUnmuted() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}

// Section CTA sits top-right across the landing page scenes
const CTA_STYLE = {
  position: 'absolute', top: 40, right: 64,
  zIndex: 30,          // above the films rail, which spans the full height
  fontFamily: 'var(--font-mono)', fontSize: 11,
  textTransform: 'uppercase', letterSpacing: '0.18em',
  color: '#ff2a1f', textDecoration: 'none',
  border: '1px solid rgba(255,42,31,0.7)',
  padding: '10px 24px',
  borderRadius: 2,
  background: 'rgba(255,42,31,0.12)',
  backdropFilter: 'blur(4px)',
  transition: 'background 200ms ease, border-color 200ms ease, color 200ms ease, box-shadow 200ms ease',
  whiteSpace: 'nowrap',
  boxShadow: '0 0 18px rgba(255,42,31,0.18)',
};

const FADE_MS = 280;         // crossfade out before the source is swapped
const HOVER_SETTLE_MS = 120;  // cursor must rest this long before a film loads

const GRAIN_SVG = "data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E";

// ─── SceneFilm — t11.com style ────────────────────────────────────────────────
export default function SceneFilm({ width = 1440, height = 900, isMobile = false }) {
  const [activeIndex,  setActiveIndex]  = useState(0);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [videoOpacity, setVideoOpacity] = useState(1);
  const [videoError,   setVideoError]   = useState(false);
  // Which film the <video> is actually on. Trails displayIndex by HOVER_SETTLE_MS
  // so scrubbing the list doesn't start a download per item.
  const [videoIndex,   setVideoIndex]   = useState(0);
  const [muted,        setMuted]        = useState(true);
  const [expanded,     setExpanded]     = useState(false);   // fullscreen player

  const [ytId, setYtId] = useState(null);   // backdrop embed, when the film has one

  const videoRef    = useRef(null);
  const modalVideoRef = useRef(null);
  const ytFrameRef  = useRef(null);
  const mutedRef    = useRef(true);
  const switchTimer = useRef(null);
  const currentSrc  = useRef('');

  const displayIndex = previewIndex ?? activeIndex;
  const film         = FILMS[displayIndex];
  const videoFilm    = FILMS[videoIndex];   // what the <video> is actually showing

  // ── Video crossfade ───────────────────────────────────────────────────────
  // A request made mid-fade supersedes the pending one rather than being dropped:
  // dropping it left the <video> on the previous film while the title and list
  // had already moved on, and nothing ever re-triggered the load.
  const switchTo = useCallback((nextFilm) => {
    const key = sourceKey(nextFilm);
    if (!key || key === currentSrc.current) return;
    clearTimeout(switchTimer.current);
    currentSrc.current = key;
    setVideoError(false);
    setVideoOpacity(0);

    switchTimer.current = setTimeout(() => {
      const v = videoRef.current;

      // ── YouTube film: park the <video>, mount the embed, fade it in once it
      // has loaded (onYtLoad) so two YouTube films in a row don't flash black.
      // The timer is the backstop if a load event never arrives.
      if (nextFilm.youtubeId) {
        if (v) { v.pause(); v.removeAttribute('src'); v.load(); }
        setYtId(nextFilm.youtubeId);
        setTimeout(() => { if (currentSrc.current === key) setVideoOpacity(1); }, 2500);
        return;
      }

      setYtId(null);
      if (!v) return;
      v.src = nextFilm.src;   // assigning src aborts any in-flight load
      v.load();
      v.play().catch(() => {});

      // Reveal once there's a frame to show. The guard keeps a slow load that
      // resolves after a newer pick from fading the wrong film back in.
      const reveal = () => { if (currentSrc.current === key) setVideoOpacity(1); };
      if (v.readyState >= 2) reveal();
      else {
        v.addEventListener('loadeddata', reveal, { once: true });
        v.addEventListener('error',      reveal, { once: true });
      }
    }, FADE_MS);
  }, []);

  useEffect(() => () => clearTimeout(switchTimer.current), []);

  // Embed finished loading: reveal it, and bring its sound in line with the
  // toggle (it always starts muted; the player needs a moment before it listens).
  const onYtLoad = useCallback(() => {
    setVideoOpacity(1);
    setTimeout(() => ytCommand(ytFrameRef.current, mutedRef.current ? 'mute' : 'unMute'), 600);
  }, []);

  // Initial load
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    currentSrc.current = sourceKey(FILMS[0]);
    if (FILMS[0].youtubeId) { setYtId(FILMS[0].youtubeId); return; }
    v.src = FILMS[0].src;
    v.load();
    v.play().catch(() => {});
  }, []);

  // Titles and list highlighting follow the cursor immediately; the video only
  // follows once the cursor settles. Re-running clears the previous timer, so a
  // fast scrub across the list loads one film, not one per item.
  useEffect(() => {
    if (videoIndex === displayIndex) return;
    const t = setTimeout(() => setVideoIndex(displayIndex), HOVER_SETTLE_MS);
    return () => clearTimeout(t);
  }, [displayIndex, videoIndex]);

  useEffect(() => {
    switchTo(FILMS[videoIndex]);
  }, [videoIndex, switchTo]);

  // React sets `muted` as a property, so drive it from state rather than JSX
  useEffect(() => {
    mutedRef.current = muted;
    const v = videoRef.current;
    if (v) v.muted = muted;
    if (ytId) ytCommand(ytFrameRef.current, muted ? 'mute' : 'unMute');
  }, [muted, ytId]);

  // Fullscreen player owns the audio while open: silence + pause the backdrop,
  // then restore it (always muted) on close.
  useEffect(() => {
    const v = videoRef.current;
    if (!expanded) {
      if (ytId) ytCommand(ytFrameRef.current, 'playVideo');
      else if (v && !videoError) v.play().catch(() => {});
      return;
    }
    setMuted(true);
    if (ytId) ytCommand(ytFrameRef.current, 'pauseVideo');
    v?.pause();

    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [expanded, videoError, ytId]);

  // Opened by a click, so audio is allowed; fall back to muted if the browser refuses
  useEffect(() => {
    if (!expanded) return;
    const mv = modalVideoRef.current;
    if (!mv) return;
    mv.muted = false;
    mv.play().catch(() => { mv.muted = true; mv.play().catch(() => {}); });
  }, [expanded]);

  // The backdrop trails the list by HOVER_SETTLE_MS, so commit to the film that
  // is actually on screen before opening it — otherwise clicking the backdrop
  // can open the previously highlighted film instead of the one playing.
  const onBackdropClick = () => {
    setPreviewIndex(null);
    setActiveIndex(videoIndex);
    setExpanded(true);
  };

  const onListEnter = (i) => setPreviewIndex(i);
  const onListLeave = ()  => setPreviewIndex(null);
  // Clicking a name commits to that film and opens the fullscreen player.
  // A click is deliberate, so it skips the hover-settle delay entirely.
  const onListClick = (i) => {
    setPreviewIndex(null);
    setActiveIndex(i);
    setVideoIndex(i);
    setExpanded(true);
  };

  // ── Mobile layout: video top half + scrollable film list ─────────────────
  if (isMobile) {
    return (
      <div style={{ width: '100%', height: '100%', background: '#050505', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header — label left, section CTA right */}
        <div style={{
          padding: '14px 20px 10px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          flexShrink: 0,
          borderBottom: '1px solid rgba(242,239,232,0.06)',
        }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.22em',
            color: 'rgba(242,239,232,0.35)', display: 'flex', gap: 10, alignItems: 'center',
          }}>
            <span style={{ color: 'rgba(255,42,31,0.70)' }}>04</span>
            <span style={{ width: 22, height: 1, background: 'rgba(242,239,232,0.18)', display: 'inline-block' }} />
            FILM
          </div>
          <TransitionLink
            to="/videography"
            style={{
              flexShrink: 0,
              fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em',
              color: '#ff2a1f', textDecoration: 'none',
              border: '1px solid rgba(255,42,31,0.55)',
              padding: '7px 14px', borderRadius: 2,
              background: 'rgba(255,42,31,0.08)',
              textTransform: 'uppercase',
            }}
          >
            ALL FILMS →
          </TransitionLink>
        </div>

        {/* Video player */}
        <div style={{ position: 'relative', flex: '0 0 46%', background: '#050505', overflow: 'hidden' }}>
          <video
            ref={videoRef}
            muted playsInline loop preload="auto"
            onError={() => setVideoError(true)}
            style={{
              width: '100%', height: '100%',
              objectFit: 'contain', background: '#050505',
              transform: videoFilm.zoom ? `scale(${videoFilm.zoom})` : undefined,
              transformOrigin: videoFilm.origin || 'center center',
              opacity: videoError || ytId ? 0 : videoOpacity,
              transition: 'opacity 560ms cubic-bezier(0.16,1,0.3,1)',
            }}
          />
          {ytId && (
            <iframe
              ref={ytFrameRef}
              key={ytId}
              src={ytBackdropSrc(ytId)}
              title={`${videoFilm.title} — ${videoFilm.sub}`}
              allow="autoplay; encrypted-media"
              onLoad={onYtLoad}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                border: 0, background: '#050505',
                pointerEvents: 'none',       // the embed is a backdrop, not a player
                opacity: videoOpacity,
                transition: 'opacity 560ms cubic-bezier(0.16,1,0.3,1)',
              }}
            />
          )}
          {videoError && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 8,
                letterSpacing: '0.22em', color: 'rgba(242,239,232,0.20)',
              }}>VIDEO UNAVAILABLE</span>
            </div>
          )}
          {/* Controls — titles removed here too; the list below names each film */}
          <div style={{
            position: 'absolute', bottom: 12, left: 16, right: 16,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', gap: 10,
          }}>
            {/* Sound toggle */}

            <button
              type="button"
              onClick={() => setMuted(m => !m)}
              aria-label={muted ? 'Unmute video' : 'Mute video'}
              aria-pressed={!muted}
              style={{
                flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, borderRadius: 2,
                appearance: 'none', cursor: 'pointer',
                color: muted ? 'rgba(242,239,232,0.6)' : '#ff2a1f',
                border: `1px solid ${muted ? 'rgba(242,239,232,0.22)' : 'rgba(255,42,31,0.75)'}`,
                background: muted ? 'rgba(4,4,4,0.5)' : 'rgba(255,42,31,0.14)',
                backdropFilter: 'blur(4px)',
              }}
            >
              {muted ? <IconMuted /> : <IconUnmuted />}
            </button>

          </div>
          {/* REC dot */}
          <div style={{
            position: 'absolute', top: 10, left: 12,
            display: 'flex', alignItems: 'center', gap: 6,
            fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '0.28em',
            color: 'rgba(242,239,232,0.28)',
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#ff2a1f', flexShrink: 0 }} />
            PLAYING
          </div>
        </div>

        {/* Film list */}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
          {FILMS.map((f, i) => {
            const isActive = i === activeIndex;
            return (
              <button
                key={f.id}
                onClick={() => onListClick(i)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', padding: '11px 20px',
                  background: isActive ? 'rgba(255,42,31,0.07)' : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid rgba(242,239,232,0.05)',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 9,
                  color: isActive ? '#ff2a1f' : 'rgba(242,239,232,0.22)',
                  width: 18, flexShrink: 0,
                }}>{String(i + 1).padStart(2, '0')}</span>
                <span style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800,
                  fontSize: 13, letterSpacing: '-0.01em', textTransform: 'uppercase',
                  color: isActive ? '#f2efe8' : 'rgba(242,239,232,0.55)',
                  flex: 1,
                }}>{f.title}</span>
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 8,
                  color: 'rgba(242,239,232,0.28)', letterSpacing: '0.14em',
                  flexShrink: 0,
                }}>{f.sub}</span>
              </button>
            );
          })}
        </div>

      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width, height, background: '#050505', overflow: 'hidden' }}>

      {/* ── Full-canvas background video (object-fit: contain — no crop) ── */}
      <video
        ref={videoRef}
        data-cursor="play"
        muted
        playsInline
        loop
        preload="auto"
        aria-hidden="true"
        onError={() => setVideoError(true)}
        onClick={onBackdropClick}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',   /* no crop — letterbox/pillarbox as needed */
          background: '#050505',
          transform: videoFilm.zoom ? `scale(${videoFilm.zoom})` : undefined,
          transformOrigin: videoFilm.origin || 'center center',
          cursor: 'pointer',
          opacity: videoError || ytId ? 0 : videoOpacity,
          transition: 'opacity 560ms cubic-bezier(0.16,1,0.3,1)',
        }}
      />

      {/* ── Same backdrop, for a film that lives on YouTube ──
          The embed ignores pointer events so the sheet above it keeps the
          click-to-fullscreen behaviour the <video> has. */}
      {ytId && (
        <>
          <iframe
            ref={ytFrameRef}
            key={ytId}
            src={ytBackdropSrc(ytId)}
            title={`${videoFilm.title} — ${videoFilm.sub}`}
            allow="autoplay; encrypted-media"
            onLoad={onYtLoad}
            style={{
              position: 'absolute', inset: 0, zIndex: 1,
              width: '100%', height: '100%',
              border: 0, background: '#050505',
              pointerEvents: 'none',
              transform: videoFilm.zoom ? `scale(${videoFilm.zoom})` : undefined,
              transformOrigin: videoFilm.origin || 'center center',
              opacity: videoOpacity,
              transition: 'opacity 560ms cubic-bezier(0.16,1,0.3,1)',
            }}
          />
          <div
            data-cursor="play"
            aria-hidden="true"
            onClick={onBackdropClick}
            style={{ position: 'absolute', inset: 0, zIndex: 1, cursor: 'pointer' }}
          />
        </>
      )}

      {/* ── Video-unavailable overlay (shown when src returns 404 / network error) */}
      {videoError && (
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0, zIndex: 8,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 18,
          pointerEvents: 'none',
        }}>
          {/* Broken-frame icon */}
          <svg width="48" height="38" viewBox="0 0 48 38" fill="none" opacity="0.18">
            <rect x="1" y="1" width="46" height="36" rx="3" stroke="#f2efe8" strokeWidth="1.5" strokeDasharray="5 3" />
            <line x1="8"  y1="8"  x2="18" y2="20" stroke="#f2efe8" strokeWidth="1.2" />
            <line x1="18" y1="8"  x2="8"  y2="20" stroke="#f2efe8" strokeWidth="1.2" />
          </svg>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 9,
            letterSpacing: '0.30em', textTransform: 'uppercase',
            color: 'rgba(242,239,232,0.22)',
          }}>
            VIDEO FILE MISSING — RE-UPLOAD TO SQUARESPACE
          </div>
        </div>
      )}

      {/* Grain */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2,
        opacity: 0.036,
        backgroundImage: `url("${GRAIN_SVG}")`,
        backgroundSize: '200px',
        mixBlendMode: 'overlay',
      }} />

      {/* Vignette */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 3,
        background: 'radial-gradient(ellipse at 60% 50%, transparent 25%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.70) 100%)',
      }} />

      {/* Left gradient — darkens for title legibility */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 4,
        background: 'linear-gradient(100deg, rgba(4,4,4,0.88) 0%, rgba(4,4,4,0.52) 35%, rgba(4,4,4,0.08) 58%, transparent 75%)',
      }} />

      {/* Right gradient — darkens right panel for title legibility */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 4,
        background: 'linear-gradient(270deg, rgba(4,4,4,0.78) 0%, rgba(4,4,4,0.42) 22%, transparent 48%)',
      }} />

      {/* Bottom gradient */}
      <div aria-hidden="true" style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%',
        pointerEvents: 'none', zIndex: 4,
        background: 'linear-gradient(180deg, transparent 0%, rgba(4,4,4,0.6) 100%)',
      }} />

      {/* ── Header row ────────────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', top: 40, left: 64,
        display: 'flex', alignItems: 'center', gap: 14,
        zIndex: 20,
        fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.22em',
        color: 'rgba(242,239,232,0.35)',
      }}>
        <span style={{ color: 'rgba(255,42,31,0.65)' }}>04</span>
        <span style={{ width: 36, height: 1, background: 'rgba(242,239,232,0.18)', display: 'inline-block', flexShrink: 0 }} />
        FILM
      </div>

      {/* ── Section CTA — top right ──────────────────────────────────── */}
      <TransitionLink
        to="/videography"
        style={CTA_STYLE}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,42,31,0.28)';
          e.currentTarget.style.borderColor = 'rgba(255,42,31,1)';
          e.currentTarget.style.color = '#fff';
          e.currentTarget.style.boxShadow = '0 0 28px rgba(255,42,31,0.42)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,42,31,0.12)';
          e.currentTarget.style.borderColor = 'rgba(255,42,31,0.7)';
          e.currentTarget.style.color = '#ff2a1f';
          e.currentTarget.style.boxShadow = '0 0 18px rgba(255,42,31,0.18)';
        }}
      >
        VIEW ALL FILMS →
      </TransitionLink>

      {/* ── REC badge ─────────────────────────────────────────────────── */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 80, left: 64, zIndex: 20,
        display: 'flex', alignItems: 'center', gap: 8,
        fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '0.34em',
        textTransform: 'uppercase', color: 'rgba(242,239,232,0.28)',
      }}>
        <span className={styles.recPulse} style={{
          display: 'block', width: 6, height: 6, borderRadius: '50%',
          background: '#ff2a1f', boxShadow: '0 0 8px rgba(255,42,31,0.8)', flexShrink: 0,
        }} />
        PLAYING
      </div>

      {/* ── Controls — bottom left (titles removed; the rail names them) ── */}
      <div style={{ position: 'absolute', bottom: 64, left: 64, zIndex: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Sound toggle */}

        <button
          type="button"
          onClick={() => setMuted(m => !m)}
          aria-label={muted ? 'Unmute video' : 'Mute video'}
          aria-pressed={!muted}
          title={muted ? 'Unmute' : 'Mute'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 9,
            appearance: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 9,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: muted ? 'rgba(242,239,232,0.55)' : '#ff2a1f',
            border: `1px solid ${muted ? 'rgba(242,239,232,0.22)' : 'rgba(255,42,31,0.75)'}`,
            background: muted ? 'rgba(4,4,4,0.42)' : 'rgba(255,42,31,0.14)',
            backdropFilter: 'blur(4px)',
            padding: '8px 14px', borderRadius: 2,
            transition: 'color 200ms ease, border-color 200ms ease, background 200ms ease',
          }}
        >
          {muted ? <IconMuted /> : <IconUnmuted />}
          {muted ? 'UNMUTE' : 'MUTE'}
        </button>

      </div>

      {/* ── Film list — right side ─────────────────────────────────────── */}
      <nav
        aria-label="Films"
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: 300,
          zIndex: 20,
          // The rail is a full-height 300px box but only its rows are visible.
          // Left clickable, its empty upper area covered the section CTA and
          // swallowed every click on it.
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {FILMS.map((f, i) => {
          const isActive  = i === activeIndex;
          const isHovered = i === previewIndex;
          return (
            <button
              key={f.id}
              className={`${styles.filmItem}${isActive ? ` ${styles.filmItemActive}` : ''}${isHovered ? ` ${styles.filmItemHovered}` : ''}`}
              style={{ pointerEvents: 'auto' }}
              onMouseEnter={() => onListEnter(i)}
              onMouseLeave={onListLeave}
              onClick={() => onListClick(i)}
              aria-label={`Play ${f.title} — ${f.sub} fullscreen with sound`}
              aria-current={isActive ? 'true' : undefined}
            >
              <span className={styles.filmItemNum}>{String(i + 1).padStart(2, '0')}</span>
              <span className={styles.filmItemName} style={nameSizeFor(f.title)}>{f.title}</span>
              <span className={styles.filmItemSub}>{f.sub}</span>
              <span className={styles.filmItemLine} aria-hidden="true" />
            </button>
          );
        })}
      </nav>

      {/* ── Fullscreen player ─────────────────────────────────────────────
          Portalled to <body>: Home scales each scene with a CSS transform,
          and a transformed ancestor makes position:fixed resolve against it
          instead of the viewport. */}
      {expanded && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${film.title} — ${film.sub}`}
          onClick={() => setExpanded(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(4,4,4,0.97)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {film.youtubeId ? (
            // a box the shape of the film, as large as the window allows
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'relative',
                aspectRatio: `${film.width} / ${film.height}`,
                width: film.height > film.width
                  ? 'min(100vw, calc(100vh * 9 / 16))'
                  : 'min(100vw, calc(100vh * 16 / 9))',
                background: '#000',
              }}
            >
              <YouTubePlayer film={film} />
            </div>
          ) : (
            <video
              ref={modalVideoRef}
              src={film.src}
              autoPlay loop playsInline controls
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
            />
          )}

          <div aria-hidden="true" style={{
            position: 'absolute', bottom: 34, left: 40, pointerEvents: 'none',
            textShadow: '0 2px 18px rgba(0,0,0,0.9)',
          }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 30,
              textTransform: 'uppercase', letterSpacing: '-0.02em', color: '#f2efe8',
            }}>{film.title}</div>
            <div style={{
              marginTop: 7, fontFamily: 'var(--font-mono)', fontSize: 9,
              letterSpacing: '0.34em', textTransform: 'uppercase', color: 'rgba(255,42,31,0.85)',
            }}>{film.sub}</div>
          </div>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
            aria-label="Close fullscreen player"
            style={{
              position: 'absolute', top: 26, right: 30,
              display: 'inline-flex', alignItems: 'center', gap: 10,
              appearance: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-mono)', fontSize: 10,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: '#ff2a1f',
              border: '1px solid rgba(255,42,31,0.7)',
              background: 'rgba(255,42,31,0.12)',
              backdropFilter: 'blur(4px)',
              padding: '9px 16px', borderRadius: 2,
            }}
          >
            ESC ✕
          </button>
        </div>,
        document.body
      )}

    </div>
  );
}
